import {AuthUser} from "../contexts/AuthContext";
import {Authorizations} from "./Authorizations";
import {ForecastLockModel, PropertyType, UserRole} from "../__generated__/generated_types";
import { accountViewFxBarUserPermissionsMap, ModelingMethodName } from "../pages/workflows/account/formula-bar/logic/utils";

export type TLockOverrides = {
    reforecast: boolean,
    budget: boolean,
}

export function checkAuthorization(user: AuthUser | null, authorization: Authorizations): boolean {
    if(!user) {
        return false;
    }

    if(user.role === UserRole.Admin) {
        return true;
    }

    return user.authorizations.includes(authorization);
}

export function hasAnyAuthorization(user: AuthUser | null, authorizations: Authorizations[]): boolean {
    if(!user) {
        return false;
    }

    if(user.role === UserRole.Admin) {
        return true;
    }

    let hasAny = false;
    for(const auth of authorizations) {
        hasAny = hasAny || user.authorizations.includes(auth);
    }
    return hasAny;
}

export function userIsInternal(user: AuthUser | null): boolean {
    if(!user) {
        return false;
    }
    return user.isInternalUser;
}

export function userIsAdmin(user: AuthUser | null): boolean {
    if(!user) {
        return false;
    }

    return user.role === UserRole.Admin;
}

export function userIsReadOnly(user: AuthUser | null):boolean {
    if(!user || user.role === UserRole.Admin) {
        return false;
    }

    return user.isReadOnly;
}

export function checkOnlyAuthorization(user: AuthUser | null, authorization: Authorizations): boolean {
    if(!user) {
        return false;
    }

    return user.authorizations.length == 0 && user.authorizations.firstElement == authorization;
}

export function canOverrideAssumptionLock(user: AuthUser, forecastLocks: ForecastLockModel): TLockOverrides {
    const userCanEditLockedAccounts = checkAuthorization(user, Authorizations.EditLockUnlockAccounts);
    const lockOverrides: TLockOverrides = {
        reforecast: true,
        budget: true,
    };

    if (forecastLocks.property.reforecastLocked || (forecastLocks.account?.reforecastLocked && !userCanEditLockedAccounts)) {
        lockOverrides.reforecast = false;
    }

    if (forecastLocks.property.budgetLocked || (forecastLocks.account?.budgetLocked && !userCanEditLockedAccounts)) {
        lockOverrides.budget = false;
    }

    return lockOverrides;
}

function isUserAModelingMethodsAdmin(user: AuthUser): boolean {
    return userIsAdmin(user)
        || checkAuthorization(user, Authorizations.ModelingMethodsAdmin)
        || checkAuthorization(user, Authorizations.AccessPropertyPlanManagement)
        || checkAuthorization(user, Authorizations.AccessBudgetSeasonManagement);
}

function isUserAModelingMethodsManager(user: AuthUser): boolean {
    return checkAuthorization(user, Authorizations.ModelingMethodsManager);
}

export function buildEditableFxBarChecker(user: AuthUser, accountLockedForVersionType: boolean): (modelingMethod: ModelingMethodName, modelingMethodLocked: boolean) => boolean {
    const canUserEditModelingMethod = (modelingMethod: ModelingMethodName, modelingMethodLocked: boolean): boolean => {
        if (user && user.role) {
            const editableModelingMethods = accountViewFxBarUserPermissionsMap[user.role];

            if (accountLockedForVersionType && !checkAuthorization(user, Authorizations.EditLockUnlockAccounts)) {
                return false;
            }

            let canEdit = false;

            if (modelingMethodLocked) {
                canEdit = isUserAModelingMethodsAdmin(user);
            }
            else {
                canEdit = isUserAModelingMethodsAdmin(user)
                    || isUserAModelingMethodsManager(user)
                    || editableModelingMethods.includes(modelingMethod);
            }

            return canEdit;
        }
        return false;
    };

    return canUserEditModelingMethod;
}

export function canUserEditWorksheetLineValues(user: AuthUser | null, lineValuesLocked: boolean): boolean {
    if(!user) {
        return false;
    }

    return isUserAModelingMethodsAdmin(user) || !lineValuesLocked; // Only admins can edit locked line values
}

export function hasAcquisitionPropertyAccess(user: AuthUser | null): boolean {
    if(!user) {
        return false;
    }

    return userIsAdmin(user) || user.acquisitionProperties.length > 0;
}
